<template>
  <section class="trainerpoint-live">
    <header class="trainerpoint-header">
      <div class="trainerpoint-title">
        <span>TRAINERPOINT LIVE</span>
        <h1>Kunden im Center</h1>
        <p>Termine planen, Betreuung dokumentieren und Kundeninformationen direkt öffnen.</p>
      </div>

      <div class="trainerpoint-header__actions">
        <span v-if="lastUpdatedLabel" class="trainerpoint-updated">{{ lastUpdatedLabel }}</span>
        <button type="button" class="icon-button" :disabled="loading" aria-label="Live-Übersicht aktualisieren" @click="fetchLiveFloor(true, true)">
          <v-icon :class="{ spinning: loading }">mdi-refresh</v-icon>
        </button>
        <button v-if="closable" type="button" class="icon-button" aria-label="Trainerpoint Live schliessen" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </button>
      </div>
    </header>

    <span class="live-update-announcement" aria-live="polite" aria-atomic="true">{{ liveUpdateAnnouncement }}</span>

    <section class="direct-customer-search" aria-label="Person direkt suchen">
      <div class="direct-customer-search__intro">
        <span class="direct-customer-search__icon"><v-icon>mdi-account-search-outline</v-icon></span>
        <div>
          <strong>Person direkt suchen</strong>
          <span>Öffnet jede Person direkt in der Kundenakte – unabhängig vom Check-in-Status.</span>
        </div>
      </div>
      <user-search class="direct-customer-search__field" ignore-rights return-user label="Name oder E-Mail eingeben" @update="selectSearchedUser" />
    </section>

    <div class="trainerpoint-toolbar">
      <div class="view-tabs" role="tablist" aria-label="Anzeige filtern">
        <button
          v-for="view in viewOptions"
          :key="view.value"
          type="button"
          role="tab"
          :aria-selected="activeView === view.value"
          :class="{ active: activeView === view.value }"
          @click="setActiveView(view.value)">
          <v-icon>{{ view.icon }}</v-icon>
          {{ view.label }}
          <span>{{ view.count }}</span>
        </button>
      </div>

      <label class="customer-search">
        <v-icon>mdi-magnify</v-icon>
        <input v-model.trim="search" type="search" autocomplete="off" placeholder="Aktuelle Liste filtern" />
        <button v-if="search" type="button" aria-label="Suche löschen" @click="search = ''">
          <v-icon>mdi-close-circle</v-icon>
        </button>
      </label>
    </div>

    <div v-if="errorMessage && !loading" class="state-message state-message--error">
      <v-icon>mdi-alert-circle-outline</v-icon>
      <div>
        <strong>Live-Übersicht konnte nicht geladen werden</strong>
        <span>{{ errorMessage }}</span>
      </div>
      <button type="button" @click="fetchLiveFloor(true)">Nochmal versuchen</button>
    </div>

    <div v-if="loading && !entries.length" class="skeleton-grid" aria-label="Live-Übersicht wird geladen">
      <div v-for="index in 8" :key="index" class="customer-skeleton"></div>
    </div>

    <div v-else-if="!visibleEntries.length" class="empty-state">
      <v-icon>{{ search ? "mdi-account-search-outline" : emptyState.icon }}</v-icon>
      <strong>{{ search ? "Keine passende Person gefunden" : emptyState.title }}</strong>
      <span>{{ search ? "Prüfe den Namen oder setze den Listenfilter zurück." : emptyState.text }}</span>
      <button v-if="search" type="button" @click="search = ''">Suche zurücksetzen</button>
    </div>

    <transition-group v-else name="checkin-card" tag="div" class="customer-grid">
      <article
        v-for="entry in visibleEntries"
        :key="entry._id"
        class="customer-card"
        :class="{
          'customer-card--live': entry.hasLiveActivity,
          'customer-card--checked-out': entryIsCheckedOut(entry),
          'customer-card--recent': isRecentEntry(entry),
          'customer-card--recent-checkout': isRecentCheckout(entry),
        }">
        <div class="customer-card__head">
          <button
            type="button"
            class="customer-avatar"
            :class="{ 'customer-avatar--initials': !getPicture(entry), 'customer-avatar--checked-out': entryIsCheckedOut(entry) }"
            :disabled="!getPicture(entry)"
            :aria-label="getPicture(entry) ? `Foto von ${displayName(entry)} vergrössern` : undefined"
            @click="openImagePreview(entry)">
            <img
              v-if="getPicture(entry)"
              :src="getPicture(entry)"
              :alt="displayName(entry)"
              width="54"
              height="54"
              loading="lazy"
              decoding="async"
              @error="markImageFailed(entry)" />
            <span v-else>{{ getInitials(entry) }}</span>
            <i aria-hidden="true"></i>
          </button>

          <div class="customer-identity">
            <h2>{{ displayName(entry) }}</h2>
            <div class="visit-meta">
              <span><v-icon>mdi-login</v-icon>{{ formatTime(entry.checkinDate) }}</span>
              <span v-if="entryIsCheckedOut(entry)" class="visit-meta__checkout">
                <v-icon>mdi-logout</v-icon>{{ formatTime(entry.checkoutDate) }}
              </span>
              <span><v-icon>mdi-clock-outline</v-icon>{{ formatVisitDuration(entry) }}</span>
            </div>
          </div>

          <button
            v-if="hasLinkedUser(entry)"
            type="button"
            class="customer-profile-link"
            aria-label="Kundenakte öffnen"
            title="Kundenakte öffnen"
            @click="selectEntry(entry)">
            <v-icon>mdi-account-arrow-right-outline</v-icon>
          </button>
        </div>

        <div v-if="entry.activityUi && entry.activityUi.length" class="activity-list" aria-label="Aktuelle Aktivitäten">
          <div
            v-for="activity in entry.activityUi"
            :key="activity.key"
            class="activity-card"
            :class="[`activity-card--${activity.tone}`, { 'activity-card--current': activity.state === 'current' }]">
            <span class="activity-card__icon"
              ><v-icon>{{ activity.icon }}</v-icon></span
            >
            <span class="activity-card__copy">
              <small>{{ activity.eyebrow }}</small>
              <strong>{{ activity.title }}</strong>
              <span>{{ activityMeta(activity) }}</span>
            </span>
            <span v-if="activity.state === 'current'" class="activity-card__live">
              <i aria-hidden="true"></i>
              LIVE
            </span>
          </div>
        </div>

        <div v-if="appointmentFor(entry) || visibleSignals(entry).length" class="signal-list">
          <button
            v-if="appointmentFor(entry)"
            type="button"
            class="signal-chip signal-chip--blue next-appointment-chip"
            :title="appointmentLabel(appointmentFor(entry))"
            @click="openExistingAppointment(entry, appointmentFor(entry))">
            <v-icon>mdi-calendar-clock</v-icon>
            {{ nextAppointmentChipLabel(entry) }}
          </button>
          <span v-for="signal in visibleSignals(entry)" :key="signal.key" class="signal-chip" :class="`signal-chip--${signal.tone}`">
            <v-icon>{{ signal.icon }}</v-icon>
            {{ signal.label }}
          </span>
        </div>

        <div v-if="entry.careUi && entry.careUi.ready" class="care-overview">
          <button
            type="button"
            class="care-status"
            :class="`care-status--${entry.careUi.trainingPlan.state}`"
            title="Trainingspläne in der Kundenakte öffnen"
            @click="openCareSection(entry, 'training')">
            <span class="care-status__icon"
              ><v-icon>{{ entry.careUi.trainingPlan.icon }}</v-icon></span
            >
            <span class="care-status__copy">
              <small>TRAININGSPLAN</small>
              <strong>{{ entry.careUi.trainingPlan.label }}</strong>
              <span>{{ entry.careUi.trainingPlan.meta }}</span>
            </span>
            <v-icon class="care-status__arrow">mdi-chevron-right</v-icon>
          </button>

          <button
            type="button"
            class="care-status"
            :class="`care-status--${entry.careUi.anamnese.state}`"
            title="Anamnese in der Kundenakte öffnen"
            @click="openCareSection(entry, 'anamnese')">
            <span class="care-status__icon"
              ><v-icon>{{ entry.careUi.anamnese.icon }}</v-icon></span
            >
            <span class="care-status__copy">
              <small>ANAMNESE</small>
              <strong>{{ entry.careUi.anamnese.label }}</strong>
              <span>{{ entry.careUi.anamnese.meta }}</span>
            </span>
            <v-icon class="care-status__arrow">mdi-chevron-right</v-icon>
          </button>
        </div>

        <div class="customer-actions">
          <button
            type="button"
            class="action-button action-button--primary"
            :disabled="!hasLinkedUser(entry)"
            @click="handleAppointmentAction(entry)">
            <v-icon>mdi-calendar-plus</v-icon>
            Termin planen
          </button>
          <button
            type="button"
            class="action-button action-button--secondary"
            :disabled="!hasLinkedUser(entry)"
            @click="$emit('create-interaction', entry)">
            <v-icon>mdi-message-text-plus-outline</v-icon>
            Interaktion
          </button>
        </div>

        <div v-if="!hasLinkedUser(entry)" class="no-account-note">
          <v-icon>mdi-account-alert-outline</v-icon>
          Dieser Check-in ist mit keinem roody-Konto verknüpft.
        </div>
      </article>
    </transition-group>

    <transition name="photo-fade">
      <div
        v-if="imagePreview"
        class="customer-photo-overlay"
        role="dialog"
        aria-modal="true"
        :aria-label="`Foto von ${imagePreview.name}`"
        @click.self="closeImagePreview">
        <div class="customer-photo-preview">
          <button type="button" aria-label="Bild schliessen" @click="closeImagePreview">
            <v-icon>mdi-close</v-icon>
          </button>
          <img :src="imagePreview.src" :alt="imagePreview.name" @error="closeImagePreview" />
          <strong>{{ imagePreview.name }}</strong>
        </div>
      </div>
    </transition>

    <div v-if="hasMoreEntries" class="load-more">
      <button type="button" @click="renderLimit += pageSize">
        Weitere {{ Math.min(pageSize, filteredEntries.length - visibleEntries.length) }} Personen anzeigen
      </button>
      <span>{{ visibleEntries.length }} von {{ filteredEntries.length }}</span>
    </div>

    <div v-if="truncated" class="limit-note">
      <v-icon>mdi-information-outline</v-icon>
      Aus Performancegründen ist die Anzahl der angezeigten Check-ins und Check-outs begrenzt.
    </div>

    <transition name="template-fade">
      <div v-if="templateChooserOpen" class="template-overlay" role="dialog" aria-modal="true" @click.self="closeTemplateChooser">
        <section class="template-sheet">
          <header class="template-sheet__header">
            <div>
              <span>TERMIN PLANEN</span>
              <h2>Was möchtest du für {{ selectedEntryName }} eintragen?</h2>
              <p>Kunde, Datum, Uhrzeit und Mitarbeiter werden im nächsten Schritt bereits vorausgefüllt.</p>
            </div>
            <button type="button" class="icon-button" aria-label="Vorlagenauswahl schliessen" @click="closeTemplateChooser">
              <v-icon>mdi-close</v-icon>
            </button>
          </header>

          <label class="template-search">
            <v-icon>mdi-magnify</v-icon>
            <input v-model.trim="templateSearch" type="search" autocomplete="off" placeholder="Terminart suchen" />
            <button v-if="templateSearch" type="button" aria-label="Suche löschen" @click="templateSearch = ''">
              <v-icon>mdi-close-circle</v-icon>
            </button>
          </label>

          <div v-if="filteredAppointmentTemplates.length" class="template-grid">
            <button
              v-for="template in filteredAppointmentTemplates"
              :key="templateKey(template)"
              type="button"
              class="template-option"
              @click="chooseAppointmentTemplate(template)">
              <span class="template-option__icon" :style="{ background: template.color || '#079653' }">
                <v-icon color="white">{{ template.icon || "mdi-calendar" }}</v-icon>
              </span>
              <span class="template-option__text">
                <strong>{{ template.title || "Termin" }}</strong>
                <small>{{ Number(template.duration) || 60 }} Minuten</small>
              </span>
              <v-icon class="template-option__arrow">mdi-chevron-right</v-icon>
            </button>
          </div>

          <div v-else class="template-empty">
            <v-icon>mdi-calendar-search-outline</v-icon>
            <strong>Keine passende Terminart gefunden</strong>
            <span>Prüfe den Suchbegriff.</span>
          </div>
        </section>
      </div>
    </transition>
  </section>
</template>

<script>
import axios from "axios"
import UserSearch from "@/components/userSearch"
import socketService from "@/socketService"

// Diese Hinweise werden bereits in einer eigenen, prominenten Anzeige dargestellt
// und deshalb nicht zusätzlich als kleine Chips wiederholt.
const SUPPRESSED_CHIP_SIGNAL_KEYS = new Set([
  "new_member",
  "appointment_soon",
  "anamnese_missing",
  "anamnese_old",
  "training_plan_missing",
  "training_plan_old",
])

export default {
  name: "WorkCalendarCheckinPool",
  components: { UserSearch },

  props: {
    closable: {
      type: Boolean,
      default: false,
    },
    appointmentTemplates: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      entries: [],
      activeView: "all",
      search: "",
      loading: false,
      errorMessage: "",
      generatedAt: null,
      fallbackRefreshAfterMs: 5 * 60 * 1000,
      fallbackRefreshTimer: null,
      socketRefreshTimer: null,
      socketRefreshQueued: false,
      clockTimer: null,
      recentEntryIds: [],
      recentCheckoutIds: [],
      recentEntryTimer: null,
      liveUpdateAnnouncement: "",
      hasLoadedLiveFloor: false,
      renderLimit: 24,
      pageSize: 24,
      failedImages: {},
      imagePreview: null,
      truncated: false,
      requestSequence: 0,
      destroyed: false,
      templateChooserOpen: false,
      selectedActionEntry: null,
      templateSearch: "",
    }
  },

  computed: {
    isMobileLayout() {
      return Boolean(this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.xsOnly)
    },

    liveSummary() {
      return this.entries.reduce(
        (summary, entry) => {
          if (this.entryIsCheckedOut(entry)) {
            summary.checkedOut += 1
            return summary
          }
          summary.present += 1
          if (entry.needsAttention) summary.attention += 1
          if (this.hasSignal(entry, "appointment_soon")) summary.appointmentsSoon += 1
          if (this.hasSignal(entry, "no_upcoming")) summary.noUpcoming += 1
          return summary
        },
        { present: 0, checkedOut: 0, attention: 0, appointmentsSoon: 0, noUpcoming: 0 },
      )
    },

    viewOptions() {
      const options = [
        { value: "all", label: "Im Center", icon: "mdi-account-multiple-outline", count: this.liveSummary.present },
        { value: "checked_out", label: "Heute ausgecheckt", icon: "mdi-logout", count: this.liveSummary.checkedOut },
        { value: "attention", label: "Empfehlungen", icon: "mdi-account-alert-outline", count: this.liveSummary.attention },
        { value: "appointments", label: "Termin bald", icon: "mdi-calendar-clock-outline", count: this.liveSummary.appointmentsSoon },
        { value: "no_upcoming", label: "Ohne Folgetermin", icon: "mdi-calendar-question-outline", count: this.liveSummary.noUpcoming },
      ]

      return this.isMobileLayout ? options.slice(0, 2) : options
    },

    filteredEntries() {
      const query = this.normalizeText(this.search)
      const entries = this.entries.filter((entry) => {
        const checkedOut = this.entryIsCheckedOut(entry)
        if (this.activeView === "checked_out" && !checkedOut) return false
        if (this.activeView !== "checked_out" && checkedOut) return false
        if (this.activeView === "attention" && !entry.needsAttention) return false
        if (this.activeView === "appointments" && !this.hasSignal(entry, "appointment_soon")) return false
        if (this.activeView === "no_upcoming" && !this.hasSignal(entry, "no_upcoming")) return false
        if (!query) return true
        return this.normalizeText(`${this.displayName(entry)} ${entry.email || ""}`).includes(query)
      })

      return entries.sort((left, right) => {
        if (this.activeView === "attention" && Number(right.priority) !== Number(left.priority)) {
          return Number(right.priority) - Number(left.priority)
        }
        const dateKey = this.activeView === "checked_out" ? "checkoutDate" : "checkinDate"
        return new Date(right[dateKey] || 0) - new Date(left[dateKey] || 0)
      })
    },

    visibleEntries() {
      return this.filteredEntries.slice(0, this.renderLimit)
    },

    hasMoreEntries() {
      return this.visibleEntries.length < this.filteredEntries.length
    },

    lastUpdatedLabel() {
      if (!this.generatedAt) return ""
      const date = new Date(this.generatedAt)
      if (Number.isNaN(date.getTime())) return ""
      return `Stand ${date.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}`
    },

    emptyState() {
      const states = {
        all: {
          icon: "mdi-account-off-outline",
          title: "Aktuell ist niemand eingecheckt",
          text: "Neue Check-ins erscheinen bei der nächsten Aktualisierung automatisch.",
        },
        checked_out: {
          icon: "mdi-logout",
          title: "Heute wurde noch niemand ausgecheckt",
          text: "Ausgecheckte Personen erscheinen hier direkt nach dem nächsten Update.",
        },
        attention: {
          icon: "mdi-check-circle-outline",
          title: "Alles im grünen Bereich",
          text: "Für die aktuell Anwesenden gibt es momentan keine konkrete Empfehlung.",
        },
        appointments: {
          icon: "mdi-calendar-clock-outline",
          title: "Kein Termin steht unmittelbar bevor",
          text: "Hier erscheinen Termine, die laufen oder innerhalb der nächsten zwei Stunden beginnen.",
        },
        no_upcoming: {
          icon: "mdi-calendar-check-outline",
          title: "Alle haben einen Folgetermin",
          text: "Aktuell ist niemand ohne kommenden Termin.",
        },
      }
      return states[this.activeView] || states.all
    },

    customerAppointmentTemplates() {
      const seen = new Set()
      return (this.appointmentTemplates || []).filter((template) => {
        if (!template || template.type === "task" || template.type === "pause" || template.requiresCustomer !== true) {
          return false
        }
        const key = this.templateKey(template)
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    },

    filteredAppointmentTemplates() {
      const query = this.normalizeText(this.templateSearch)
      if (!query) return this.customerAppointmentTemplates
      return this.customerAppointmentTemplates.filter((template) =>
        this.normalizeText(`${template.title || ""} ${template.specialType || ""}`).includes(query),
      )
    },

    selectedEntryName() {
      return this.selectedActionEntry ? this.displayName(this.selectedActionEntry) : "diesen Kunden"
    },
  },

  watch: {
    search() {
      this.renderLimit = this.pageSize
    },
    isMobileLayout(isMobile) {
      if (isMobile && !["all", "checked_out"].includes(this.activeView)) this.setActiveView("all")
    },
  },

  created() {
    this.fetchLiveFloor(true)
  },

  mounted() {
    document.addEventListener("visibilitychange", this.handleVisibilityChange, { passive: true })
    this.setupLiveFloorSocket()
    this.startFallbackRefreshTimer()
    this.clockTimer = window.setInterval(this.refreshDurations, 60000)
  },

  beforeDestroy() {
    this.destroyed = true
    document.removeEventListener("visibilitychange", this.handleVisibilityChange)
    this.teardownLiveFloorSocket()
    window.clearInterval(this.fallbackRefreshTimer)
    window.clearTimeout(this.socketRefreshTimer)
    window.clearTimeout(this.recentEntryTimer)
    window.clearInterval(this.clockTimer)
  },

  methods: {
    entryKey(entry) {
      return String(entry?._id || "")
    },

    isRecentEntry(entry) {
      const key = this.entryKey(entry)
      return Boolean(key && this.recentEntryIds.includes(key))
    },

    isRecentCheckout(entry) {
      const key = this.entryKey(entry)
      return Boolean(key && this.recentCheckoutIds.includes(key))
    },

    trackLiveEntryChanges(nextEntries) {
      const previousState = new Map(
        this.entries.map((entry) => [this.entryKey(entry), { checkedOut: this.entryIsCheckedOut(entry) }]).filter(([key]) => Boolean(key)),
      )
      const arrivedIds = []
      const checkedOutIds = []

      nextEntries.forEach((entry) => {
        const key = this.entryKey(entry)
        if (!key) return
        const previous = previousState.get(key)
        const checkedOut = this.entryIsCheckedOut(entry)

        if (!checkedOut && (!previous || previous.checkedOut)) arrivedIds.push(key)
        if (checkedOut && previous && !previous.checkedOut) checkedOutIds.push(key)
      })

      if (!arrivedIds.length && !checkedOutIds.length) return

      const recentIds = new Set(this.recentEntryIds)
      const recentCheckoutIds = new Set(this.recentCheckoutIds)
      checkedOutIds.forEach((key) => recentIds.delete(key))
      arrivedIds.forEach((key) => recentCheckoutIds.delete(key))
      arrivedIds.forEach((key) => recentIds.add(key))
      checkedOutIds.forEach((key) => recentCheckoutIds.add(key))
      this.recentEntryIds = [...recentIds]
      this.recentCheckoutIds = [...recentCheckoutIds]

      const announcements = []
      if (arrivedIds.length) announcements.push(arrivedIds.length === 1 ? "Ein neuer Check-in" : `${arrivedIds.length} neue Check-ins`)
      if (checkedOutIds.length) announcements.push(checkedOutIds.length === 1 ? "Ein Check-out" : `${checkedOutIds.length} Check-outs`)
      this.liveUpdateAnnouncement = `Trainerpoint aktualisiert: ${announcements.join(", ")}.`

      window.clearTimeout(this.recentEntryTimer)
      this.recentEntryTimer = window.setTimeout(() => {
        this.recentEntryIds = []
        this.recentCheckoutIds = []
        this.liveUpdateAnnouncement = ""
      }, 2600)
    },

    async fetchLiveFloor(force = false, refreshCare = false) {
      if (this.loading && !force) return
      const sequence = ++this.requestSequence
      this.loading = true
      this.errorMessage = ""

      try {
        const response = await axios.get("https://app.wellcomefit.ch/api/checkin/pool/live-floor", {
          headers: { Authorization: localStorage.getItem("token") },
          params: refreshCare ? { refreshCare: true } : undefined,
        })
        if (this.destroyed || sequence !== this.requestSequence) return

        const appointmentLookaheadDays = Number(response.data?.appointmentLookaheadDays) || 0
        const nextEntries = Array.isArray(response.data?.entries)
          ? response.data.entries.map((entry) =>
              this.decorateEntry({
                ...entry,
                appointmentLookaheadDays,
              }),
            )
          : []
        if (this.hasLoadedLiveFloor) this.trackLiveEntryChanges(nextEntries)
        this.entries = nextEntries
        this.hasLoadedLiveFloor = true
        this.generatedAt = response.data?.generatedAt || new Date().toISOString()
        this.truncated = Boolean(response.data?.truncated)
        this.renderLimit = Math.max(this.pageSize, Math.min(this.renderLimit, this.entries.length || this.pageSize))
        this.startFallbackRefreshTimer()
      } catch (error) {
        if (this.destroyed || sequence !== this.requestSequence) return
        this.errorMessage = error?.response?.data?.message || "Bitte prüfe die Verbindung und versuche es erneut."
        console.error("Fehler beim Laden des Trainerpoint Live-Pools:", error)
      } finally {
        if (!this.destroyed && sequence === this.requestSequence) this.loading = false
      }
    },

    fetchCheckinPool() {
      return this.fetchLiveFloor(true)
    },

    setupLiveFloorSocket() {
      socketService.on("checkin_pool_updated", this.scheduleSocketRefresh)
      socketService.on("connect", this.scheduleSocketRefresh)
    },

    teardownLiveFloorSocket() {
      socketService.off("checkin_pool_updated", this.scheduleSocketRefresh)
      socketService.off("connect", this.scheduleSocketRefresh)
    },

    scheduleSocketRefresh() {
      if (this.destroyed) return
      if (typeof document !== "undefined" && document.hidden) {
        this.socketRefreshQueued = true
        return
      }

      window.clearTimeout(this.socketRefreshTimer)
      this.socketRefreshTimer = window.setTimeout(() => {
        this.socketRefreshQueued = false
        // force verhindert, dass ein Pool-Event waehrend einer aelteren Anfrage verloren geht.
        this.fetchLiveFloor(true)
      }, 250)
    },

    startFallbackRefreshTimer() {
      window.clearInterval(this.fallbackRefreshTimer)
      if (typeof document !== "undefined" && document.hidden) return
      // Sicherheitsnetz fuer verpasste Events; die eigentliche Aktualisierung ist Socket-getrieben.
      this.fallbackRefreshTimer = window.setInterval(() => {
        if (!document.hidden) this.fetchLiveFloor(false)
      }, this.fallbackRefreshAfterMs)
    },

    handleVisibilityChange() {
      if (document.hidden) {
        window.clearInterval(this.fallbackRefreshTimer)
        return
      }
      this.socketRefreshQueued = false
      this.fetchLiveFloor(true)
      this.startFallbackRefreshTimer()
    },

    refreshDurations() {
      if (!this.entries.length) return
      const now = Date.now()
      this.entries = this.entries.map((entry) => {
        const started = new Date(entry.checkinDate)
        if (Number.isNaN(started.getTime())) return entry
        const checkout = new Date(entry.checkoutDate)
        const endedAt = this.entryIsCheckedOut(entry) && !Number.isNaN(checkout.getTime()) ? checkout.getTime() : now
        return { ...entry, minutesInStudio: Math.max(0, Math.floor((endedAt - started.getTime()) / 60000)) }
      })
    },

    setActiveView(value) {
      this.activeView = value
      this.renderLimit = this.pageSize
    },

    hasSignal(entry, key) {
      return Array.isArray(entry?.signals) && entry.signals.some((signal) => signal.key === key)
    },

    visibleSignals(entry) {
      return (Array.isArray(entry?.signals) ? entry.signals : []).filter((signal) => !SUPPRESSED_CHIP_SIGNAL_KEYS.has(signal.key)).slice(0, 3)
    },

    hasLinkedUser(entry) {
      return Boolean(entry?.user?._id)
    },

    entryIsCheckedOut(entry) {
      if (entry?.isCheckedOut === true) return true
      if (entry?.isCheckedOut === false) return false
      const checkin = new Date(entry?.checkinDate)
      const checkout = new Date(entry?.checkoutDate)
      if (Number.isNaN(checkout.getTime())) return false
      return Number.isNaN(checkin.getTime()) || checkout.getTime() >= checkin.getTime()
    },

    selectSearchedUser(user) {
      const userId = user?._id
      if (!userId) return

      const existingEntry = this.entries.find((entry) => String(entry?.user?._id || "") === String(userId))
      const entry = existingEntry || {
        _id: `user-search-${userId}`,
        user: {
          _id: userId,
          vorname: user.vorname || "",
          nachname: user.nachname || "",
          bild: user.bild || "",
        },
        firstName: user.vorname || "",
        lastName: user.nachname || "",
        email: user.email || "",
        checkinDate: null,
        checkoutDate: null,
        isCheckedOut: false,
      }

      this.selectEntry(entry, { source: "user-search" })
    },

    appointmentFor(entry) {
      return entry?.todayAppointment || entry?.nextAppointment || null
    },

    handleAppointmentAction(entry) {
      if (!this.hasLinkedUser(entry)) return
      this.openTemplateChooser(entry)
    },

    openExistingAppointment(entry, appointment) {
      if (!appointment?._id) return
      this.$emit("open-appointment", { entry, appointment })
    },

    openImagePreview(entry) {
      const src = this.getPicture(entry)
      if (!src) return
      this.imagePreview = { src, name: this.displayName(entry) }
    },

    closeImagePreview() {
      this.imagePreview = null
    },

    openCareSection(entry, section) {
      if (section === "training") {
        this.selectEntry(entry, { section: "training" })
        return
      }
      this.selectEntry(entry, { action: "anamnese" })
    },

    openTemplateChooser(entry) {
      if (!this.hasLinkedUser(entry)) return
      if (!this.customerAppointmentTemplates.length) {
        this.$toast?.warning("Es wurde keine Kundentermin-Vorlage gefunden.")
        return
      }
      this.selectedActionEntry = entry
      this.templateSearch = ""
      this.templateChooserOpen = true
    },

    closeTemplateChooser() {
      this.templateChooserOpen = false
      this.templateSearch = ""
      this.selectedActionEntry = null
    },

    chooseAppointmentTemplate(template) {
      if (!this.selectedActionEntry || !template) return
      const entry = this.selectedActionEntry
      this.closeTemplateChooser()
      this.$emit("create-appointment", { entry, template })
    },

    selectEntry(entry, options = {}) {
      if (!this.hasLinkedUser(entry)) {
        this.$toast?.warning("Dieser Check-in ist mit keinem roody-Konto verknüpft.")
        return
      }
      this.$emit("entry-selected", entry, options)
    },

    decorateEntry(entry) {
      const isCheckedOut = this.entryIsCheckedOut(entry)
      const careUi = this.buildCareUi(entry)
      const activityUi = isCheckedOut ? [] : this.buildActivityUi(entry?.activities)
      const signals = (Array.isArray(entry?.signals) ? entry.signals : []).filter(
        (signal) => signal?.key !== "no_upcoming" || Number(entry?.appointmentLookaheadDays) >= 180,
      )
      return {
        ...entry,
        isCheckedOut,
        signals,
        needsAttention: !isCheckedOut && signals.length > 0,
        priority: signals[0]?.priority || 0,
        careUi,
        activityUi,
        hasLiveActivity: activityUi.some((activity) => activity.state === "current"),
      }
    },

    buildActivityUi(activities) {
      const definitions = {
        workout_v2: {
          icon: "mdi-arm-flex-outline",
          tone: "training",
          currentLabel: "TRAINING LÄUFT",
          upcomingLabel: "TRAINING",
        },
        groupfitness: {
          icon: "mdi-account-group-outline",
          tone: "course",
          currentLabel: "IM GROUPFITNESS-KURS",
          upcomingLabel: "GROUPFITNESS HEUTE",
        },
        reservation: {
          icon: "mdi-spa-outline",
          tone: "reservation",
          currentLabel: "RESERVATION LÄUFT",
          upcomingLabel: "HEUTE RESERVIERT",
        },
      }

      return (Array.isArray(activities) ? activities : [])
        .filter((activity) => definitions[activity?.type])
        .slice(0, 3)
        .map((activity, index) => {
          const definition = definitions[activity.type]
          return {
            ...activity,
            key: activity.sourceId || `${activity.type}-${activity.start || index}`,
            icon: definition.icon,
            tone: definition.tone,
            eyebrow: activity.state === "current" ? definition.currentLabel : definition.upcomingLabel,
            title: activity.title || "Aktivität",
          }
        })
    },

    activityMeta(activity) {
      const start = new Date(activity?.start)
      const end = new Date(activity?.end)
      const hasStart = !Number.isNaN(start.getTime())
      const hasEnd = !Number.isNaN(end.getTime())

      if (activity?.type === "workout_v2" && activity?.state === "current") {
        const elapsedMinutes = hasStart ? Math.max(0, Math.floor((Date.now() - start.getTime()) / 60000)) : 0
        return elapsedMinutes < 60
          ? `Seit ${elapsedMinutes} Min. aktiv`
          : `Seit ${Math.floor(elapsedMinutes / 60)} Std.${elapsedMinutes % 60 ? ` ${elapsedMinutes % 60} Min.` : ""} aktiv`
      }

      if (activity?.state === "current") {
        return hasEnd ? `Jetzt · bis ${this.formatTime(end)}` : "Läuft gerade"
      }

      if (hasStart && hasEnd) return `${this.formatTime(start)}–${this.formatTime(end)}`
      if (hasStart) return `Ab ${this.formatTime(start)}`
      return "Heute"
    },

    buildCareUi(entry) {
      const care = entry?.care
      if (!care) return { ready: false }

      const trainingPlan = care.trainingPlan || {}
      const anamnese = care.anamnese || {}
      const planAge =
        trainingPlan.ageDays !== null && trainingPlan.ageDays !== undefined && Number.isFinite(Number(trainingPlan.ageDays))
          ? Number(trainingPlan.ageDays)
          : null
      const anamneseAge =
        anamnese.ageDays !== null && anamnese.ageDays !== undefined && Number.isFinite(Number(anamnese.ageDays)) ? Number(anamnese.ageDays) : null
      const planState =
        trainingPlan.available === null || trainingPlan.available === undefined
          ? "unavailable"
          : trainingPlan.available === false
          ? "missing"
          : planAge !== null && planAge >= 180
          ? "stale"
          : "current"
      const anamneseState =
        anamnese.available === null || anamnese.available === undefined
          ? "unavailable"
          : anamnese.available === false
          ? "missing"
          : anamneseAge !== null && anamneseAge >= 365
          ? "stale"
          : "current"

      return {
        ready: true,
        trainingPlan: {
          state: planState,
          icon:
            planState === "unavailable"
              ? "mdi-database-alert-outline"
              : planState === "missing"
              ? "mdi-dumbbell"
              : planState === "stale"
              ? "mdi-calendar-refresh-outline"
              : "mdi-arm-flex-outline",
          label:
            planState === "unavailable"
              ? "Status nicht geladen"
              : trainingPlan.available === false
              ? "Nicht vorhanden"
              : trainingPlan.title || (trainingPlan.type === "split" ? "Split-Plan verfügbar" : "Trainingsplan verfügbar"),
          meta:
            planState === "unavailable"
              ? "Über Aktualisieren erneut prüfen"
              : trainingPlan.available === false
              ? "Direkt einen Plan erstellen"
              : this.careTimestampMeta("Aktualisiert", trainingPlan.updatedAt, planAge),
        },
        anamnese: {
          state: anamneseState,
          icon:
            anamneseState === "unavailable"
              ? "mdi-database-alert-outline"
              : anamneseState === "missing"
              ? "mdi-clipboard-pulse-outline"
              : anamneseState === "stale"
              ? "mdi-clipboard-edit-outline"
              : "mdi-clipboard-check-outline",
          label: anamneseState === "unavailable" ? "Status nicht geladen" : anamnese.available === false ? "Nicht vorhanden" : "Anamnese verfügbar",
          meta:
            anamneseState === "unavailable"
              ? "Über Aktualisieren erneut prüfen"
              : anamnese.available === false
              ? "Beim Gespräch gemeinsam erfassen"
              : this.careTimestampMeta("Erfasst", anamnese.updatedAt, anamneseAge),
        },
      }
    },

    careTimestampMeta(prefix, value, ageDays) {
      const date = new Date(value)
      const dateLabel = Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "2-digit" })
      const ageLabel = this.formatCareAge(ageDays)
      return [prefix, ageLabel, dateLabel ? `· ${dateLabel}` : ""].filter(Boolean).join(" ")
    },

    formatCareAge(value) {
      const days = Number(value)
      if (!Number.isFinite(days)) return ""
      if (days <= 0) return "heute"
      if (days === 1) return "gestern"
      if (days < 14) return `vor ${days} Tagen`
      if (days < 60) return `vor ${Math.round(days / 7)} Wochen`
      if (days < 365) return `vor ${Math.round(days / 30)} Monaten`
      const years = Math.max(1, Math.round(days / 365))
      return years === 1 ? "vor 1 Jahr" : `vor ${years} Jahren`
    },

    templateKey(template) {
      return String(template?._id || template?.id || `${template?.title || "termin"}-${template?.specialType || ""}`)
    },

    normalizeText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase("de-CH")
    },

    displayName(entry) {
      const firstName = entry?.firstName || entry?.user?.vorname || ""
      const lastName = entry?.lastName || entry?.user?.nachname || ""
      return `${firstName} ${lastName}`.trim() || "Unbekannter Check-in"
    },

    getInitials(entry) {
      const name = this.displayName(entry).split(/\s+/).filter(Boolean)
      return `${name[0]?.[0] || "?"}${name.length > 1 ? name[name.length - 1][0] : ""}`.toUpperCase()
    },

    getPicture(entry) {
      const userId = entry?.user?._id
      const failureState = this.failedImages[entry?._id]
      if (!userId || failureState === "tac") return ""

      if (entry.user.bild && failureState !== "profile") {
        const webp = String(entry.user.bild)
          .replace(/\.[^/.]+$/, ".webp")
          .replace(/^\/+/, "")
        return `/img/profilbilder/${webp}`
      }

      return `https://app.wellcomefit.ch/api/checkin/pool/images/${encodeURIComponent(userId)}`
    },

    markImageFailed(entry) {
      const failureState = this.failedImages[entry?._id]
      if (entry?.user?.bild && failureState !== "profile") {
        this.$set(this.failedImages, entry._id, "profile")
        return
      }
      this.$set(this.failedImages, entry._id, "tac")
    },

    formatTime(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return "–"
      return date.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },

    formatVisitDuration(entry) {
      const total = Math.max(0, Number(entry.minutesInStudio) || 0)
      const suffix = this.entryIsCheckedOut(entry) ? "Aufenthalt" : "im Center"
      if (total < 60) return `${total} Min. ${suffix}`
      const hours = Math.floor(total / 60)
      const minutes = total % 60
      return `${hours} Std.${minutes ? ` ${minutes} Min.` : ""} ${suffix}`
    },

    localDateKey(value = new Date()) {
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) return ""
      const pad = (number) => String(number).padStart(2, "0")
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    },

    appointmentLabel(appointment) {
      if (!appointment) return ""
      const date = new Date(`${appointment.date}T12:00:00`)
      const dateLabel = appointment.date === this.localDateKey() ? "" : `${date.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" })} · `
      return `${dateLabel}${appointment.start || ""}–${appointment.end || ""} · ${appointment.title || "Termin"}`
    },

    nextAppointmentChipLabel(entry) {
      const appointment = this.appointmentFor(entry)
      if (!appointment?.date) return "Nächster Termin"

      const soonSignal = (entry.signals || []).find((signal) => signal.key === "appointment_soon")
      if (soonSignal?.label) return soonSignal.label

      const appointmentDate = new Date(`${appointment.date}T12:00:00`)
      if (Number.isNaN(appointmentDate.getTime())) return "Nächster Termin"
      const today = new Date()
      today.setHours(12, 0, 0, 0)
      const days = Math.max(0, Math.round((appointmentDate.getTime() - today.getTime()) / 86400000))
      if (days === 0) return `Nächster Termin heute${appointment.start ? ` um ${appointment.start}` : ""}`
      if (days === 1) return "Nächster Termin morgen"
      return `Nächster Termin in ${days} Tagen`
    },
  },
}
</script>

<style scoped>
.trainerpoint-live {
  --tp-green: #079653;
  --tp-green-dark: #067541;
  --tp-ink: #17251f;
  --tp-muted: #65786e;
  --tp-line: #dce8e2;
  min-height: 100%;
  padding: 22px clamp(14px, 2.4vw, 34px) 36px;
  color: var(--tp-ink);
  background: #f3f7f5;
  font-size: 14px;
}

.trainerpoint-live *,
.trainerpoint-live *::before,
.trainerpoint-live *::after {
  box-sizing: border-box;
}

.trainerpoint-live button,
.trainerpoint-live input {
  font-family: inherit;
}

.live-update-announcement {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.trainerpoint-header,
.trainerpoint-header__actions,
.trainerpoint-toolbar,
.customer-card__head,
.visit-meta,
.customer-actions {
  display: flex;
  align-items: center;
}

.trainerpoint-header {
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
  padding: 4px 2px 14px;
  border-bottom: 1px solid rgba(121, 151, 135, 0.2);
}

.trainerpoint-title > span,
.template-sheet__header span {
  display: block;
  color: var(--tp-green);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.14em;
}

.trainerpoint-title h1 {
  margin: 2px 0 3px;
  font-size: clamp(27px, 3vw, 38px);
  line-height: 1.05;
  letter-spacing: -0.035em;
}

.trainerpoint-title p,
.template-sheet__header p {
  margin: 0;
  color: var(--tp-muted);
  font-size: 14px;
}

.trainerpoint-header__actions {
  gap: 9px;
}

.trainerpoint-updated {
  color: var(--tp-muted);
  font-size: 12px;
  white-space: nowrap;
}

.direct-customer-search {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(320px, 480px);
  align-items: center;
  gap: 18px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #bfe0cf;
  border-radius: 16px;
  background: linear-gradient(135deg, #eaf7f0, #f8fcfa);
}

.direct-customer-search__intro {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 11px;
}

.direct-customer-search__icon {
  display: grid;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  place-items: center;
  border-radius: 13px;
  background: #d5eee1;
}

.direct-customer-search__icon >>> .v-icon {
  color: var(--tp-green-dark);
}

.direct-customer-search__intro div {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.direct-customer-search__intro strong {
  font-size: 14px;
}

.direct-customer-search__intro div > span {
  color: var(--tp-muted);
  font-size: 12px;
}

.direct-customer-search__field {
  margin: 0 !important;
  padding: 0 !important;
}

.direct-customer-search__field >>> .v-input__slot {
  min-height: 48px !important;
  margin-bottom: 0 !important;
  background: #fff !important;
}

.direct-customer-search__field >>> .v-text-field__details {
  display: none;
}

.icon-button {
  display: inline-grid;
  width: 48px;
  height: 48px;
  place-items: center;
  border: 1px solid var(--tp-line);
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
}

.icon-button:disabled {
  cursor: default;
  opacity: 0.5;
}

.icon-button >>> .v-icon {
  color: #50655a;
}

.spinning {
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.trainerpoint-toolbar {
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
  padding: 8px;
  border: 1px solid var(--tp-line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 8px 24px rgba(38, 70, 53, 0.045);
}

.view-tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
}

.view-tabs > button {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  gap: 7px;
  padding: 0 13px;
  color: var(--tp-muted);
  font-size: 13px;
  font-weight: 800;
  border: 0;
  border-radius: 13px;
  background: transparent;
  cursor: pointer;
}

.view-tabs > button.active {
  color: #fff;
  background: var(--tp-green);
  box-shadow: 0 8px 18px rgba(7, 150, 83, 0.2);
}

.view-tabs > button >>> .v-icon {
  color: currentColor;
  font-size: 19px;
}

.view-tabs > button span {
  min-width: 23px;
  padding: 2px 6px;
  text-align: center;
  border-radius: 999px;
  background: rgba(101, 120, 110, 0.12);
}

.view-tabs > button.active span {
  background: rgba(255, 255, 255, 0.2);
}

.customer-search,
.template-search {
  display: flex;
  min-height: 44px;
  align-items: center;
  gap: 9px;
  padding: 0 12px;
  border: 1px solid var(--tp-line);
  border-radius: 14px;
  background: #fff;
}

.customer-search {
  width: min(340px, 35vw);
}

.customer-search >>> .v-icon,
.template-search >>> .v-icon {
  color: #7d8d84;
  font-size: 21px;
}

.customer-search input,
.template-search input {
  width: 100%;
  min-width: 0;
  color: var(--tp-ink);
  font-size: 14px;
  border: 0;
  outline: 0;
  background: transparent;
}

.customer-search button,
.template-search button {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  place-items: center;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.customer-grid,
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.customer-card {
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  overflow: visible;
  border: 1px solid #dbe7e1;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(38, 70, 53, 0.055);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.customer-card::before {
  position: absolute;
  top: -1px;
  right: 20px;
  left: 20px;
  height: 3px;
  border-radius: 0 0 4px 4px;
  background: transparent;
  content: "";
}

.customer-card--live {
  border-color: #a8d9c1;
  box-shadow: 0 14px 34px rgba(7, 150, 83, 0.095);
}

.customer-card--live::before {
  background: #0aa35c;
}

.customer-card--checked-out {
  border-color: #d8e0dc;
  background: #fbfcfb;
  box-shadow: 0 8px 22px rgba(38, 70, 53, 0.04);
}

.customer-card--checked-out::before {
  background: #9aa8a1;
}

.checkin-card-enter-active,
.checkin-card-leave-active {
  transition: opacity 0.22s ease, transform 0.34s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: opacity, transform;
}

.checkin-card-enter {
  opacity: 0;
  transform: translateY(14px) scale(0.975);
}

.checkin-card-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.985);
}

.checkin-card-move {
  transition: transform 0.34s cubic-bezier(0.22, 1, 0.36, 1);
}

.customer-card--recent {
  --tp-update-ring: 7, 150, 83;
}

.customer-card--recent-checkout {
  --tp-update-ring: 119, 137, 127;
}

.customer-card--recent::after,
.customer-card--recent-checkout::after {
  position: absolute;
  z-index: 2;
  inset: -2px;
  border: 2px solid rgba(var(--tp-update-ring), 0.7);
  border-radius: 22px;
  box-shadow: 0 0 0 0 rgba(var(--tp-update-ring), 0.2);
  content: "";
  pointer-events: none;
  animation: checkin-arrival-pulse 1.8s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes checkin-arrival-pulse {
  0% {
    opacity: 0;
    box-shadow: 0 0 0 0 rgba(var(--tp-update-ring), 0.24);
    transform: scale(0.985);
  }
  22% {
    opacity: 1;
  }
  70% {
    opacity: 0.68;
    box-shadow: 0 0 0 8px rgba(var(--tp-update-ring), 0.06);
  }
  100% {
    opacity: 0;
    box-shadow: 0 0 0 12px rgba(var(--tp-update-ring), 0);
    transform: scale(1.015);
  }
}

.customer-card__head {
  gap: 10px;
  min-width: 0;
}

.customer-avatar {
  position: relative;
  display: grid;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  place-items: center;
  overflow: visible;
  padding: 0;
  border: 0;
  border-radius: 14px;
  background: #dceae3;
  cursor: zoom-in;
}

.customer-avatar:disabled {
  cursor: default;
}

.customer-avatar img {
  position: relative;
  z-index: 1;
  width: 46px;
  height: 46px;
  object-fit: cover;
  border-radius: 14px;
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}

.customer-avatar > span {
  color: #17623f;
  font-size: 17px;
  font-weight: 900;
}

.customer-avatar i {
  position: absolute;
  right: -3px;
  bottom: -3px;
  width: 14px;
  height: 14px;
  border: 3px solid #fff;
  border-radius: 50%;
  background: #12ad64;
  box-shadow: 0 0 0 1px rgba(18, 173, 100, 0.14);
  z-index: 3;
}

.customer-avatar--checked-out i {
  background: #8b9992;
  box-shadow: 0 0 0 1px rgba(85, 103, 94, 0.16);
}

.customer-identity {
  min-width: 0;
  flex: 1;
}

.customer-identity h2 {
  margin: 0 0 3px;
  overflow: hidden;
  font-size: 18px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-profile-link {
  display: inline-grid;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  place-items: center;
  padding: 0;
  color: #426456;
  border: 1px solid var(--tp-line);
  border-radius: 11px;
  background: #f7faf8;
  cursor: pointer;
}

.customer-profile-link >>> .v-icon {
  color: currentColor;
  font-size: 19px;
}

.visit-meta {
  flex-wrap: wrap;
  gap: 6px 12px;
  color: var(--tp-muted);
}

.visit-meta span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.visit-meta >>> .v-icon {
  color: #7a8e83;
  font-size: 15px;
}

.visit-meta .visit-meta__checkout {
  color: #52675c;
  font-weight: 800;
}

.activity-list {
  display: grid;
  gap: 6px;
}

.activity-card {
  --activity-accent: #148d58;
  --activity-soft: #eaf7f0;
  display: grid;
  min-width: 0;
  min-height: 58px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 9px;
  padding: 8px 10px;
  border: 1px solid #dce9e2;
  border-radius: 14px;
  background: var(--activity-soft);
}

.activity-card--training {
  --activity-accent: #078c4d;
  --activity-soft: #e8f7ef;
}

.activity-card--course {
  --activity-accent: #6251af;
  --activity-soft: #f0edfb;
}

.activity-card--reservation {
  --activity-accent: #2379a7;
  --activity-soft: #eaf5fb;
}

.activity-card__icon {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  place-items: center;
  color: var(--activity-accent);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: inset 0 0 0 1px rgba(53, 91, 71, 0.08);
}

.activity-card__icon >>> .v-icon {
  color: currentColor;
  font-size: 19px;
}

.activity-card__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.activity-card__copy small {
  color: var(--activity-accent);
  font-size: 12px;
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: 0.055em;
}

.activity-card__copy strong,
.activity-card__copy span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-card__copy strong {
  margin-top: 2px;
  color: #183226;
  font-size: 14px;
  line-height: 1.15;
}

.activity-card__copy span {
  margin-top: 1px;
  color: #61766b;
  font-size: 12px;
  line-height: 1.15;
}

.activity-card__live {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--activity-accent);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
}

.activity-card__live i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 4px rgba(7, 150, 83, 0.13);
}

.signal-list {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.signal-chip {
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  color: #53675d;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid #dce7e1;
  border-radius: 10px;
  background: #f4f8f6;
}

.signal-chip >>> .v-icon {
  color: currentColor;
  font-size: 16px;
}

.next-appointment-chip {
  cursor: pointer;
}

.next-appointment-chip:hover,
.next-appointment-chip:focus-visible {
  border-color: #80c7ee;
  background: #e2f4fd;
  outline: none;
}

.customer-photo-overlay {
  position: fixed;
  z-index: 2450;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(15, 29, 22, 0.68);
  backdrop-filter: blur(5px);
}

.customer-photo-preview {
  position: relative;
  display: flex;
  width: min(520px, 88vw);
  max-height: 88vh;
  align-items: center;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.38);
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 30px 80px rgba(6, 30, 18, 0.36);
}

.customer-photo-preview img {
  width: 100%;
  max-height: calc(88vh - 74px);
  object-fit: contain;
  border-radius: 17px;
  background: #edf4f0;
}

.customer-photo-preview strong {
  color: var(--tp-ink);
  font-size: 15px;
}

.customer-photo-preview > button {
  position: absolute;
  z-index: 2;
  top: 20px;
  right: 20px;
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 1px solid rgba(23, 37, 31, 0.12);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 5px 16px rgba(23, 37, 31, 0.14);
  cursor: pointer;
}

.customer-photo-preview > button >>> .v-icon {
  color: #31453b;
}

.photo-fade-enter-active,
.photo-fade-leave-active {
  transition: opacity 0.16s ease;
}

.photo-fade-enter,
.photo-fade-leave-to {
  opacity: 0;
}

.signal-chip--blue {
  color: #176fae;
  border-color: #cce7f8;
  background: #edf8fe;
}

.signal-chip--green {
  color: #087b46;
  border-color: #c8ead9;
  background: #edf9f3;
}

.signal-chip--amber {
  color: #a46800;
  border-color: #f4dbad;
  background: #fff7e7;
}

.signal-chip--violet {
  color: #7043a0;
  border-color: #e0ceed;
  background: #f8f0fc;
}

.signal-chip--pink {
  color: #ad316b;
  border-color: #f0c9dc;
  background: #fff1f8;
}

.signal-chip--red {
  color: #b53a3f;
  border-color: #f0c6c8;
  background: #fff1f2;
}

.care-overview {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.care-status {
  display: flex;
  min-width: 0;
  min-height: 54px;
  align-items: center;
  gap: 7px;
  padding: 7px 8px;
  text-align: left;
  border: 1px solid #dfe8e3;
  border-radius: 12px;
  background: #fbfdfc;
  cursor: pointer;
}

.care-status__icon {
  display: grid;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  place-items: center;
  color: #087b46;
  border-radius: 9px;
  background: #e7f4ed;
}

.care-status__icon >>> .v-icon {
  color: currentColor;
  font-size: 18px;
}

.care-status__copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.care-status__copy small {
  color: #718179;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: 0.05em;
}

.care-status__copy strong,
.care-status__copy span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.care-status__copy strong {
  color: #20362b;
  font-size: 13px;
  line-height: 1.2;
}

.care-status__copy span {
  color: #728079;
  font-size: 12px;
  line-height: 1.15;
}

.care-status__arrow {
  flex: 0 0 auto;
  color: #729082 !important;
  font-size: 18px !important;
}

.care-status--stale {
  border-color: #eadfc8;
  background: #fff;
}

.care-status--stale .care-status__icon {
  color: #a56800;
  background: #fbf0d9;
}

.care-status--stale .care-status__copy strong {
  color: #624d27;
}

.care-status--missing {
  border-color: #ead8da;
  background: #fff;
}

.care-status--missing .care-status__icon {
  color: #b53a3f;
  background: #fae9ea;
}

.care-status--missing .care-status__copy strong {
  color: #71383c;
}

.care-status--unavailable {
  border-color: #d8e0dc;
  background: #f6f8f7;
}

.care-status--unavailable .care-status__icon {
  color: #687970;
  background: #e5ebe8;
}

.care-status--unavailable .care-status__copy strong {
  color: #526158;
}

.trainer-recommendation {
  --step-accent: #b77a13;
  --step-soft: #fff7e7;
  display: grid;
  width: 100%;
  min-height: 48px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  text-align: left;
  color: var(--step-accent);
  border: 1px solid #dfe8e3;
  border-left: 3px solid var(--step-accent);
  border-radius: 12px;
  background: var(--step-soft);
  cursor: pointer;
}

.trainer-recommendation__icon {
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  color: var(--step-accent);
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.78);
}

.trainer-recommendation__icon >>> .v-icon {
  color: currentColor;
  font-size: 18px;
}

.trainer-recommendation__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.trainer-recommendation__copy small {
  color: #7a8981;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.05;
  letter-spacing: 0.06em;
}

.trainer-recommendation__copy strong {
  color: #24392f;
  font-size: 13px;
  line-height: 1.2;
}

.trainer-recommendation__copy span {
  display: none;
}

.trainer-recommendation__action {
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  gap: 3px;
  padding: 0 8px;
  color: var(--step-accent);
  font-size: 12px;
  font-weight: 900;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.88);
  white-space: nowrap;
}

.trainer-recommendation__action >>> .v-icon {
  color: currentColor;
  font-size: 17px;
}

.trainer-recommendation--red {
  --step-accent: #b74449;
  --step-soft: #fff5f5;
}

.trainer-recommendation--red .trainer-recommendation__icon {
  color: var(--step-accent);
  background: #fae8e9;
}

.trainer-recommendation--red .trainer-recommendation__copy strong {
  color: #24392f;
}

.trainer-recommendation--red .trainer-recommendation__copy span {
  color: #855b5e;
}

.trainer-recommendation--blue {
  --step-accent: #2379aa;
  --step-soft: #f0f8fc;
}

.trainer-recommendation--blue .trainer-recommendation__icon {
  color: var(--step-accent);
  background: #e3f1f9;
}

.trainer-recommendation--blue .trainer-recommendation__copy strong {
  color: #24392f;
}

.trainer-recommendation--blue .trainer-recommendation__copy span {
  color: #5b7484;
}

.trainer-recommendation--violet {
  --step-accent: #6d53a5;
  --step-soft: #f6f3fb;
}

.trainer-recommendation--violet .trainer-recommendation__icon {
  color: var(--step-accent);
  background: #eee8f7;
}

.trainer-recommendation--violet .trainer-recommendation__copy strong {
  color: #24392f;
}

.trainer-recommendation--violet .trainer-recommendation__copy span {
  color: #74627f;
}

.trainer-recommendation--green {
  --step-accent: #087b46;
  --step-soft: #eef8f3;
}

.trainer-recommendation--green .trainer-recommendation__icon {
  color: #087b46;
  background: #d9eee3;
}

.trainer-recommendation--green .trainer-recommendation__copy strong {
  color: #24392f;
}

.trainer-recommendation--green .trainer-recommendation__copy span {
  color: #5d7468;
}

.customer-actions {
  gap: 6px;
  margin-top: auto;
}

.action-button {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 11px;
  font-size: 13px;
  font-weight: 900;
  border-radius: 12px;
  cursor: pointer;
}

.action-button:disabled {
  cursor: default;
  filter: grayscale(0.4);
  opacity: 0.42;
}

.action-button >>> .v-icon {
  color: currentColor;
  font-size: 20px;
}

.action-button--primary {
  flex: 1.15;
  color: #fff;
  border: 1px solid var(--tp-green);
  background: #079653;
  box-shadow: 0 8px 16px rgba(7, 150, 83, 0.17);
}

.action-button--secondary {
  flex: 1;
  color: #1e6c49;
  border: 1px solid #bcdcca;
  background: #eef8f3;
}

.no-account-note {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #9b5e0b;
  font-size: 12px;
}

.no-account-note >>> .v-icon {
  color: #c78220;
  font-size: 17px;
}

.state-message,
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 1px solid var(--tp-line);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.82);
}

.state-message {
  min-height: 90px;
  gap: 12px;
  padding: 18px;
}

.state-message > div,
.empty-state {
  flex-direction: column;
}

.state-message strong,
.state-message span,
.empty-state strong,
.empty-state span {
  display: block;
  font-size: 13px;
}

.state-message span,
.empty-state span {
  color: var(--tp-muted);
}

.state-message--error >>> .v-icon {
  color: #d43c45;
}

.state-message button,
.empty-state button,
.load-more button {
  min-height: 44px;
  padding: 0 16px;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  border: 0;
  border-radius: 12px;
  background: var(--tp-green);
  cursor: pointer;
}

.empty-state {
  min-height: 260px;
  gap: 7px;
  padding: 28px;
}

.empty-state >>> .v-icon {
  margin-bottom: 4px;
  color: #82918a;
  font-size: 45px;
}

.empty-state button {
  margin-top: 9px;
}

.customer-skeleton {
  min-height: 285px;
  border: 1px solid #e0eae5;
  border-radius: 21px;
  background: #edf3f0;
}

.load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
}

.load-more span,
.limit-note {
  color: var(--tp-muted);
  font-size: 12px;
}

.limit-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 14px;
}

.limit-note >>> .v-icon {
  color: #7b8d83;
  font-size: 18px;
}

.template-overlay {
  position: fixed;
  z-index: 2300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px;
  background: rgba(20, 35, 28, 0.62);
  backdrop-filter: blur(5px);
}

.template-sheet {
  display: flex;
  width: min(900px, 100%);
  max-height: min(820px, calc(100vh - 44px));
  flex-direction: column;
  padding: 22px;
  overflow: hidden;
  border: 1px solid #dce8e2;
  border-radius: 25px;
  background: #f8fbf9;
  box-shadow: 0 30px 70px rgba(20, 45, 32, 0.25);
}

.template-sheet__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
}

.template-sheet__header h2 {
  margin: 3px 0 4px;
  font-size: 24px;
}

.template-search {
  width: 100%;
  margin: 18px 0 14px;
}

.template-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  padding: 1px 3px 5px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.template-option {
  display: flex;
  min-height: 78px;
  align-items: center;
  gap: 11px;
  padding: 11px;
  text-align: left;
  border: 1px solid #dce8e2;
  border-radius: 17px;
  background: #fff;
  cursor: pointer;
}

.template-option__icon {
  display: grid;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  place-items: center;
  border-radius: 13px;
}

.template-option__icon >>> .v-icon {
  font-size: 22px;
}

.template-option__text {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.template-option__text strong {
  overflow: hidden;
  color: var(--tp-ink);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-option__text small {
  color: var(--tp-muted);
  font-size: 12px;
}

.template-option__arrow {
  color: #789087 !important;
  font-size: 19px !important;
}

.template-empty {
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--tp-muted);
}

.template-empty >>> .v-icon {
  margin-bottom: 8px;
  color: #80958a;
  font-size: 42px;
}

.template-empty strong,
.template-empty span {
  font-size: 13px;
}

.template-fade-enter-active,
.template-fade-leave-active {
  transition: opacity 0.16s ease;
}

.template-fade-enter,
.template-fade-leave-to {
  opacity: 0;
}

@media (hover: hover) and (pointer: fine) {
  .customer-card:hover {
    border-color: #c8dad1;
    box-shadow: 0 16px 38px rgba(38, 70, 53, 0.085);
    transform: translateY(-1px);
  }

  .customer-avatar:not(:disabled):hover,
  .customer-avatar:not(:disabled):focus-visible {
    z-index: 20;
    outline: none;
  }

  .customer-avatar:not(:disabled):hover img,
  .customer-avatar:not(:disabled):focus-visible img {
    z-index: 20;
    transform: scale(2.85);
    transform-origin: top left;
    box-shadow: 0 15px 34px rgba(22, 52, 37, 0.3);
  }

  .template-option:hover,
  .care-status:hover,
  .trainer-recommendation:hover,
  .action-button:hover:not(:disabled),
  .view-tabs > button:hover {
    transform: translateY(-1px);
    filter: brightness(0.985);
  }
}

@media (max-width: 1240px) {
  .customer-grid,
  .skeleton-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .direct-customer-search {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .trainerpoint-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .customer-search {
    width: 100%;
  }

  .template-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .trainerpoint-live {
    padding: 16px 14px 28px;
  }

  .customer-grid,
  .skeleton-grid {
    grid-template-columns: 1fr;
  }

  .view-tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .view-tabs > button {
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .trainerpoint-header {
    align-items: flex-start;
  }

  .trainerpoint-title p,
  .trainerpoint-updated {
    display: none;
  }

  .direct-customer-search {
    padding: 10px;
  }

  .direct-customer-search__intro div > span {
    display: none;
  }

  .trainerpoint-header__actions {
    gap: 6px;
  }

  .customer-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .care-overview {
    grid-template-columns: 1fr;
  }

  .trainer-recommendation {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .trainer-recommendation__action {
    min-height: 32px;
    grid-column: 2;
    justify-self: start;
  }

  .activity-card {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .activity-card__live {
    grid-column: 2;
    justify-self: start;
  }

  .action-button {
    padding: 0 9px;
  }

  .template-overlay {
    align-items: flex-end;
    padding: 0;
  }

  .template-sheet {
    width: 100%;
    max-height: 92vh;
    padding: 18px 14px;
    border-radius: 24px 24px 0 0;
  }

  .template-sheet__header h2 {
    font-size: 20px;
  }

  .template-sheet__header p {
    display: none;
  }

  .template-grid {
    grid-template-columns: 1fr;
  }
}

@media (pointer: coarse) {
  .trainerpoint-live *,
  .trainerpoint-live *::before,
  .trainerpoint-live *::after {
    transition: none !important;
  }

  .trainerpoint-live .checkin-card-enter-active,
  .trainerpoint-live .checkin-card-leave-active {
    transition: opacity 0.22s ease, transform 0.34s cubic-bezier(0.22, 1, 0.36, 1) !important;
  }

  .trainerpoint-live .checkin-card-move {
    transition: transform 0.34s cubic-bezier(0.22, 1, 0.36, 1) !important;
  }

  .spinning,
  .customer-skeleton {
    animation-duration: 1.8s;
  }
}

@media (prefers-reduced-motion: reduce) {
  .trainerpoint-live .checkin-card-enter-active,
  .trainerpoint-live .checkin-card-leave-active,
  .trainerpoint-live .checkin-card-move {
    transition: none !important;
  }

  .customer-card--recent::after,
  .customer-card--recent-checkout::after {
    display: none;
    animation: none;
  }

  .spinning,
  .customer-skeleton {
    animation: none;
  }
}
</style>

<style scoped media="not all">
@media not all {
  /*
 * Trainerpoint Live – Premium visual system
 * Bewusst als eigenständige, letzte Style-Ebene gehalten: Die bestehende
 * Funktionalität bleibt unangetastet, die Oberfläche erhält jedoch eine
 * vollständig neue Hierarchie und Designsprache.
 */
  .trainerpoint-live {
    --award-ink: #10251b;
    --award-muted: #687c71;
    --award-green: #0aa864;
    --award-green-dark: #087346;
    --award-line: #dbe7e1;
    min-height: 100%;
    padding: 18px clamp(12px, 2vw, 30px) 42px;
    color: var(--award-ink);
    background: radial-gradient(circle at 4% 5%, rgba(9, 168, 100, 0.08), transparent 26rem), linear-gradient(180deg, #f6faf8 0%, #edf4f0 100%);
  }

  .trainerpoint-header {
    position: relative;
    min-height: 160px;
    align-items: flex-start;
    margin: 0;
    padding: 27px 28px 52px;
    overflow: hidden;
    color: #fff;
    border: 0;
    border-radius: 30px;
    background: radial-gradient(circle at 82% 14%, rgba(78, 232, 158, 0.25), transparent 19rem),
      radial-gradient(circle at 98% 100%, rgba(79, 164, 219, 0.17), transparent 22rem), linear-gradient(125deg, #081d15 0%, #0a3223 48%, #07583a 100%);
    box-shadow: 0 24px 56px rgba(8, 45, 30, 0.17);
  }

  .trainerpoint-header::before,
  .trainerpoint-header::after {
    position: absolute;
    pointer-events: none;
    content: "";
  }

  .trainerpoint-header::before {
    top: -135px;
    right: 4%;
    width: 360px;
    height: 360px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 50%;
  }

  .trainerpoint-header::after {
    right: -52px;
    bottom: -125px;
    width: 280px;
    height: 280px;
    border: 54px solid rgba(255, 255, 255, 0.035);
    border-radius: 50%;
  }

  .trainerpoint-title,
  .trainerpoint-header__actions {
    position: relative;
    z-index: 1;
  }

  .trainerpoint-title > span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #7ff1b9;
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 0.15em;
  }

  .trainerpoint-title > span i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #4ce698;
    box-shadow: 0 0 0 5px rgba(76, 230, 152, 0.12);
  }

  .trainerpoint-title h1 {
    margin: 7px 0 6px;
    color: #fff;
    font-size: clamp(30px, 3vw, 44px);
    font-weight: 900;
    line-height: 1;
    letter-spacing: -0.045em;
  }

  .trainerpoint-title p {
    max-width: 720px;
    margin: 0;
    color: rgba(232, 247, 239, 0.76);
    font-size: 14px;
    line-height: 1.45;
  }

  .trainerpoint-header__actions {
    gap: 8px;
  }

  .trainerpoint-updated {
    margin-right: 5px;
    color: rgba(235, 248, 241, 0.7);
    font-size: 12px;
    font-weight: 700;
  }

  .trainerpoint-header .icon-button {
    width: 46px;
    height: 46px;
    color: #fff;
    border-color: rgba(255, 255, 255, 0.16);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.09);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.09);
  }

  .trainerpoint-header .icon-button >>> .v-icon {
    color: #fff;
  }

  .trainerpoint-toolbar {
    position: relative;
    z-index: 3;
    min-height: 64px;
    margin: -31px 20px 22px;
    padding: 8px;
    border: 1px solid rgba(214, 229, 221, 0.9);
    border-radius: 20px;
    background: #fff;
    box-shadow: 0 17px 42px rgba(31, 70, 50, 0.12);
  }

  .view-tabs {
    gap: 4px;
  }

  .view-tabs > button {
    min-height: 46px;
    padding: 0 14px;
    color: #6d7e75;
    font-size: 13px;
    border-radius: 13px;
  }

  .view-tabs > button.active {
    color: #fff;
    background: linear-gradient(135deg, #0b3122, #087f4c);
    box-shadow: 0 8px 20px rgba(8, 106, 65, 0.2);
  }

  .view-tabs > button span {
    min-width: 24px;
    padding: 2px 6px;
    color: inherit;
    font-size: 12px;
    background: rgba(68, 98, 82, 0.1);
  }

  .customer-search {
    width: min(360px, 35vw);
    min-height: 46px;
    border-color: #dbe6e0;
    border-radius: 13px;
    background: #f7faf8;
  }

  .customer-search:focus-within {
    border-color: #73c89f;
    background: #fff;
    box-shadow: 0 0 0 4px rgba(10, 168, 100, 0.08);
  }

  .customer-grid,
  .skeleton-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
  }

  .customer-card {
    position: relative;
    gap: 0;
    padding: 0;
    overflow: visible;
    border: 1px solid rgba(212, 226, 218, 0.94);
    border-radius: 24px;
    background: #fff;
    box-shadow: 0 13px 35px rgba(31, 67, 48, 0.07);
  }

  .customer-card::before {
    top: 0;
    right: 24px;
    left: 24px;
    height: 3px;
    border-radius: 0 0 5px 5px;
    background: linear-gradient(90deg, transparent, #b8cec2, transparent);
  }

  .customer-card--live {
    border-color: #abd9c2;
    box-shadow: 0 16px 40px rgba(8, 120, 71, 0.11);
  }

  .customer-card--live::before {
    background: linear-gradient(90deg, transparent, #0cc270, transparent);
  }

  .customer-card__head {
    min-height: 90px;
    gap: 12px;
    padding: 17px 17px 13px;
  }

  .customer-avatar {
    width: 56px;
    height: 56px;
    flex: 0 0 56px;
    border: 3px solid #fff;
    border-radius: 50%;
    background: linear-gradient(135deg, #d8eee3, #edf6f1);
    box-shadow: 0 0 0 1px #d9e7df, 0 7px 17px rgba(28, 70, 49, 0.12);
  }

  .customer-avatar img {
    width: 50px;
    height: 50px;
    border-radius: 50%;
  }

  .customer-avatar > span {
    color: #105b3b;
    font-size: 17px;
  }

  .customer-avatar i {
    right: 0;
    bottom: 0;
    width: 14px;
    height: 14px;
    border: 3px solid #fff;
    background: #10bb6b;
    box-shadow: 0 0 0 1px rgba(7, 133, 76, 0.15);
  }

  .customer-identity h2 {
    margin-bottom: 6px;
    color: #12291e;
    font-size: 18px;
    font-weight: 900;
    letter-spacing: -0.02em;
  }

  .visit-meta {
    gap: 6px 12px;
  }

  .visit-meta span {
    color: #74857c;
    font-size: 12px;
    font-weight: 650;
  }

  .visit-meta__duration i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #10ad64;
  }

  .customer-file-shortcut {
    display: grid;
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    place-items: center;
    padding: 0;
    color: #1a6444;
    border: 1px solid #d9e7df;
    border-radius: 12px;
    background: #f5faf7;
    cursor: pointer;
  }

  .customer-file-shortcut:disabled {
    cursor: default;
    opacity: 0.35;
  }

  .customer-file-shortcut >>> .v-icon {
    color: currentColor;
    font-size: 20px;
  }

  .signal-list {
    gap: 6px;
    padding: 0 17px 12px;
  }

  .signal-chip {
    min-height: 27px;
    padding: 3px 9px;
    color: #5d7066;
    font-size: 12px;
    border-color: #dfe8e3;
    border-radius: 999px;
    background: #f7faf8;
  }

  .signal-chip--blue {
    color: #166b96;
    border-color: #cde5f1;
    background: #eef8fc;
  }

  .signal-chip--amber {
    color: #90600d;
    border-color: #ead8b5;
    background: #fff8eb;
  }

  .signal-chip--pink {
    color: #a53f70;
    border-color: #ead2df;
    background: #fff5fa;
  }

  .signal-chip--violet {
    color: #654b93;
    border-color: #ddd3eb;
    background: #f8f5fb;
  }

  .signal-chip--green {
    color: #087849;
    border-color: #cde7d9;
    background: #f1faf5;
  }

  .signal-chip--red {
    color: #a94449;
    border-color: #ead2d4;
    background: #fff6f6;
  }

  .activity-list {
    gap: 7px;
    padding: 0 12px 12px;
  }

  .activity-card {
    min-height: 62px;
    padding: 9px 11px;
    border: 1px solid #dce8e2;
    border-radius: 15px;
    background: #f6f9f7;
    box-shadow: none;
  }

  .activity-card--current {
    min-height: 68px;
    color: #fff;
    border-color: transparent;
    background: radial-gradient(circle at 90% 20%, rgba(255, 255, 255, 0.15), transparent 8rem), linear-gradient(125deg, #09291c, #087a4a);
    box-shadow: 0 12px 25px rgba(7, 96, 57, 0.18);
  }

  .activity-card--course.activity-card--current {
    background: radial-gradient(circle at 90% 20%, rgba(255, 255, 255, 0.15), transparent 8rem), linear-gradient(125deg, #26204b, #6650a7);
    box-shadow: 0 12px 25px rgba(75, 57, 132, 0.18);
  }

  .activity-card--reservation.activity-card--current {
    background: radial-gradient(circle at 90% 20%, rgba(255, 255, 255, 0.16), transparent 8rem), linear-gradient(125deg, #12364a, #247ca3);
    box-shadow: 0 12px 25px rgba(33, 107, 141, 0.18);
  }

  .activity-card__icon {
    width: 38px;
    height: 38px;
    flex: 0 0 38px;
    color: var(--activity-accent);
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 4px 12px rgba(27, 66, 46, 0.08);
  }

  .activity-card--current .activity-card__icon {
    color: #fff;
    background: rgba(255, 255, 255, 0.13);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
  }

  .activity-card__copy small {
    color: var(--activity-accent);
    font-size: 12px;
  }

  .activity-card__copy strong {
    color: #173025;
    font-size: 14px;
  }

  .activity-card__copy span {
    color: #6c7d74;
    font-size: 12px;
  }

  .activity-card--current .activity-card__copy small,
  .activity-card--current .activity-card__copy span {
    color: rgba(237, 252, 244, 0.75);
  }

  .activity-card--current .activity-card__copy strong,
  .activity-card--current .activity-card__live {
    color: #fff;
  }

  .activity-card__live {
    min-height: 26px;
    padding: 0 8px;
    color: var(--activity-accent);
    font-size: 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
  }

  .activity-card__live i {
    width: 7px;
    height: 7px;
    background: #5cf0a4;
    box-shadow: none;
  }

  .care-overview {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0;
    margin: 0 12px;
    padding: 12px 4px 11px;
    border: 1px solid #e1eae5;
    border-radius: 16px;
    background: #f7faf8;
  }

  .care-overview__heading {
    display: flex;
    grid-column: 1 / -1;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 0 10px 9px;
    border-bottom: 1px solid #e2eae6;
  }

  .care-overview__heading span {
    color: #3f574b;
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 0.08em;
  }

  .care-overview__heading small {
    color: #839188;
    font-size: 12px;
    font-weight: 650;
  }

  .care-status,
  .care-status--stale,
  .care-status--missing,
  .care-status--unavailable {
    min-height: 58px;
    gap: 8px;
    padding: 10px;
    border: 0;
    border-radius: 0;
    background: transparent;
  }

  .care-status + .care-status {
    border-left: 1px solid #e2eae6;
  }

  .care-status__icon {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    color: #07824c;
    border-radius: 11px;
    background: #e2f2e9;
  }

  .care-status--missing .care-status__icon {
    color: #bd4a4f;
    background: #f8e9ea;
  }

  .care-status--stale .care-status__icon {
    color: #a56c13;
    background: #f8eedc;
  }

  .care-status--unavailable .care-status__icon {
    color: #6f7e76;
    background: #e8eeeb;
  }

  .care-status__copy small {
    color: #718178;
    font-size: 12px;
    letter-spacing: 0.06em;
  }

  .care-status__copy strong,
  .care-status--missing .care-status__copy strong,
  .care-status--stale .care-status__copy strong,
  .care-status--unavailable .care-status__copy strong {
    color: #1b3428;
    font-size: 13px;
  }

  .care-status__copy span {
    color: #75847c;
    font-size: 12px;
  }

  .care-status__arrow {
    color: #91a097 !important;
  }

  .trainer-recommendation,
  .trainer-recommendation--red,
  .trainer-recommendation--blue,
  .trainer-recommendation--violet,
  .trainer-recommendation--green {
    min-height: 54px;
    margin: 11px 12px 0;
    padding: 8px 10px;
    border: 1px solid #dfe8e3;
    border-left: 0;
    border-radius: 15px;
    background: #fff;
    box-shadow: inset 3px 0 0 var(--step-accent);
  }

  .trainer-recommendation__icon {
    width: 34px;
    height: 34px;
    color: var(--step-accent);
    border-radius: 11px;
    background: var(--step-soft);
  }

  .trainer-recommendation__copy small {
    color: #839087;
    font-size: 12px;
  }

  .trainer-recommendation__copy strong,
  .trainer-recommendation--red .trainer-recommendation__copy strong,
  .trainer-recommendation--blue .trainer-recommendation__copy strong,
  .trainer-recommendation--violet .trainer-recommendation__copy strong,
  .trainer-recommendation--green .trainer-recommendation__copy strong {
    color: #183226;
    font-size: 13px;
  }

  .trainer-recommendation__action {
    min-height: 34px;
    padding: 0 10px;
    color: var(--step-accent);
    font-size: 12px;
    border: 1px solid #e2e9e5;
    background: #f8faf9;
  }

  .customer-actions {
    display: grid;
    grid-template-columns: 1.15fr 1fr;
    gap: 7px;
    margin-top: auto;
    padding: 14px 12px 12px;
  }

  .action-button {
    min-height: 46px;
    padding: 0 12px;
    font-size: 13px;
    border-radius: 13px;
  }

  .action-button--primary {
    color: #fff;
    border-color: #087c4a;
    background: linear-gradient(135deg, #0b3122, #07975a);
    box-shadow: 0 9px 20px rgba(7, 125, 74, 0.18);
  }

  .action-button--secondary {
    color: #175f40;
    border-color: #cfe1d7;
    background: #f3f9f6;
  }

  .no-account-note {
    margin: 0 12px 12px;
    padding: 10px;
    border-radius: 12px;
    background: #fff8eb;
  }

  .state-message,
  .empty-state {
    border-color: #d8e6df;
    border-radius: 24px;
    background: #fff;
    box-shadow: 0 14px 35px rgba(31, 67, 48, 0.06);
  }

  .customer-skeleton {
    min-height: 335px;
    border-radius: 24px;
  }

  .load-more button,
  .state-message button,
  .empty-state button {
    background: linear-gradient(135deg, #0b3122, #07975a);
  }

  .template-overlay,
  .customer-photo-overlay {
    background: rgba(7, 25, 17, 0.74);
  }

  .template-sheet {
    border-radius: 28px;
    background: #f7faf8;
    box-shadow: 0 35px 90px rgba(5, 31, 19, 0.34);
  }

  .template-option {
    border-color: #dce8e2;
    border-radius: 18px;
    box-shadow: 0 7px 18px rgba(31, 67, 48, 0.045);
  }

  @media (hover: hover) and (pointer: fine) {
    .customer-card:hover {
      border-color: #b9d3c6;
      box-shadow: 0 22px 50px rgba(26, 70, 46, 0.12);
      transform: translateY(-3px);
    }

    .customer-file-shortcut:hover,
    .care-status:hover,
    .trainer-recommendation:hover,
    .action-button:hover:not(:disabled) {
      filter: none;
      transform: translateY(-1px);
    }

    .customer-avatar:not(:disabled):hover img,
    .customer-avatar:not(:disabled):focus-visible img {
      transform: scale(2.65);
      transform-origin: top left;
      border-radius: 18px;
    }
  }

  @media (max-width: 1320px) {
    .customer-grid,
    .skeleton-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 1080px) {
    .trainerpoint-header {
      min-height: 150px;
    }

    .trainerpoint-toolbar {
      align-items: stretch;
      flex-direction: column;
      margin-right: 14px;
      margin-left: 14px;
    }

    .view-tabs {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .view-tabs > button {
      justify-content: center;
      padding: 0 8px;
    }

    .customer-search {
      width: 100%;
    }
  }

  @media (max-width: 820px) {
    .trainerpoint-live {
      padding: 10px 10px 28px;
    }

    .trainerpoint-header {
      min-height: 148px;
      padding: 22px 20px 48px;
      border-radius: 24px;
    }

    .trainerpoint-title p,
    .trainerpoint-updated {
      display: none;
    }

    .trainerpoint-title h1 {
      font-size: 31px;
    }

    .trainerpoint-toolbar {
      margin-top: -28px;
      margin-bottom: 16px;
      border-radius: 18px;
    }

    .customer-grid,
    .skeleton-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 600px) {
    .trainerpoint-header__actions {
      gap: 5px;
    }

    .trainerpoint-header .icon-button {
      width: 42px;
      height: 42px;
    }

    .view-tabs {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .customer-card__head {
      padding: 15px 14px 12px;
    }

    .signal-list {
      padding-right: 14px;
      padding-left: 14px;
    }

    .activity-list,
    .customer-actions {
      padding-right: 10px;
      padding-left: 10px;
    }

    .care-overview,
    .trainer-recommendation {
      margin-right: 10px;
      margin-left: 10px;
    }

    .care-overview {
      grid-template-columns: 1fr;
    }

    .care-status + .care-status {
      border-top: 1px solid #e2eae6;
      border-left: 0;
    }

    .activity-card {
      grid-template-columns: auto minmax(0, 1fr) auto;
    }

    .activity-card__live {
      grid-column: auto;
    }

    .customer-actions {
      grid-template-columns: 1fr 1fr;
    }
  }
}
</style>
